// Browser-owned calculation port; parity fixtures retain the original domain rules.

const COUNTERS_BY_MESSAGE_TYPE = Object.freeze({
  'canopy.telemetry': [
    {
      path: ['sabalController', 'energy', 'production', 'inverterRegisters', 'lifetimeKwh'],
      equipmentIdPath: ['sabalController', 'energy', 'production', 'equipmentId'],
      sampleTimePath: ['sabalController', 'energy', 'production', 'sampleTime'],
      dataStatusPath: ['sabalController', 'energy', 'production', 'dataStatus'],
      metric: 'solar.inverter.lifetime', unit: 'kWh'
    }
  ],
  'bess.telemetry': [
    {
      path: ['bess', 'energy', 'chargedLifetimeKwh'], equipmentIdPath: ['bess', 'energy', 'equipmentId'],
      sampleTimePath: ['bess', 'energy', 'sampleTime'], dataStatusPath: ['bess', 'energy', 'dataStatus'],
      metric: 'bess.charged.lifetime', unit: 'kWh',
      excludedContractStatuses: ['simulatorExpandedV2'],
      contractStatusPath: ['bess', 'contractStatus']
    },
    {
      path: ['bess', 'energy', 'dischargedLifetimeKwh'], equipmentIdPath: ['bess', 'energy', 'equipmentId'],
      sampleTimePath: ['bess', 'energy', 'sampleTime'], dataStatusPath: ['bess', 'energy', 'dataStatus'],
      metric: 'bess.discharged.lifetime', unit: 'kWh',
      excludedContractStatuses: ['simulatorExpandedV2'],
      contractStatusPath: ['bess', 'contractStatus']
    },
    {
      path: ['bess', 'energy', 'pcsAcChargeKwh'], equipmentIdPath: ['bess', 'energy', 'equipmentId'],
      sampleTimePath: ['bess', 'energy', 'sampleTime'], dataStatusPath: ['bess', 'energy', 'dataStatus'],
      metric: 'bess.charged.lifetime', unit: 'kWh',
      contractStatus: 'simulatorExpandedV2', contractStatusPath: ['bess', 'contractStatus']
    },
    {
      path: ['bess', 'energy', 'pcsAcDischargeKwh'], equipmentIdPath: ['bess', 'energy', 'equipmentId'],
      sampleTimePath: ['bess', 'energy', 'sampleTime'], dataStatusPath: ['bess', 'energy', 'dataStatus'],
      metric: 'bess.discharged.lifetime', unit: 'kWh',
      contractStatus: 'simulatorExpandedV2', contractStatusPath: ['bess', 'contractStatus']
    },
    {
      path: ['bess', 'energy', 'bopAuxiliaryKwh'], equipmentIdPath: ['bess', 'equipmentId'],
      sampleTimePath: ['bess', 'energy', 'sampleTime'], dataStatusPath: ['bess', 'energy', 'dataStatus'],
      metric: 'bess.bop.auxiliary.lifetime', unit: 'kWh',
      contractStatus: 'simulatorExpandedV2', contractStatusPath: ['bess', 'contractStatus']
    },
    {
      path: ['bess', 'energy', 'netSiteAbsorptionKwh'], equipmentIdPath: ['bess', 'equipmentId'],
      sampleTimePath: ['bess', 'energy', 'sampleTime'], dataStatusPath: ['bess', 'energy', 'dataStatus'],
      metric: 'bess.net_site.absorption.lifetime', unit: 'kWh',
      contractStatus: 'simulatorExpandedV2', contractStatusPath: ['bess', 'contractStatus']
    },
    {
      path: ['bess', 'energy', 'netSiteDeliveryKwh'], equipmentIdPath: ['bess', 'equipmentId'],
      sampleTimePath: ['bess', 'energy', 'sampleTime'], dataStatusPath: ['bess', 'energy', 'dataStatus'],
      metric: 'bess.net_site.delivery.lifetime', unit: 'kWh',
      contractStatus: 'simulatorExpandedV2', contractStatusPath: ['bess', 'contractStatus']
    }
  ],
  'load.telemetry': [
    {
      path: ['load', 'energy', 'servedLifetimeKwh'], equipmentIdPath: ['load', 'energy', 'equipmentId'],
      sampleTimePath: ['load', 'energy', 'sampleTime'], dataStatusPath: ['load', 'energy', 'dataStatus'],
      metric: 'load.served.lifetime', unit: 'kWh'
    }
  ],
  'pcc.telemetry': [
    {
      path: ['pcc', 'energy', 'importedLifetimeKwh'], equipmentIdPath: ['pcc', 'energy', 'equipmentId'],
      sampleTimePath: ['pcc', 'energy', 'sampleTime'], dataStatusPath: ['pcc', 'energy', 'dataStatus'],
      metric: 'pcc.imported.lifetime', unit: 'kWh'
    },
    {
      path: ['pcc', 'energy', 'exportedLifetimeKwh'], equipmentIdPath: ['pcc', 'energy', 'equipmentId'],
      sampleTimePath: ['pcc', 'energy', 'sampleTime'], dataStatusPath: ['pcc', 'energy', 'dataStatus'],
      metric: 'pcc.exported.lifetime', unit: 'kWh'
    }
  ]
});

function valueAtPath(document, path) {
  return path.reduce((value, key) => (
    value !== null && typeof value === 'object' ? value[key] : undefined
  ), document);
}

/**
 * A decrease in a known lifetime counter is a deterministic discontinuity.
 * Reset versus rollover cannot be asserted without an effective device maximum
 * or explicit device signal, so the event remains unresolved. Absent or null
 * values produce no event rather than inventing continuity.
 *
 * Section sampleTime and dataStatus are preserved directly from the section:
 * explicit null or absent sampleTime becomes null (never packet timestamp),
 * and absent dataStatus becomes null (never synthesized 'available').
 */
function counterReadings(messageType, currentPayload) {
  const definitions = COUNTERS_BY_MESSAGE_TYPE[messageType] || [];
  return definitions.flatMap((definition) => {
    const contractStatus = definition.contractStatusPath
      ? valueAtPath(currentPayload, definition.contractStatusPath)
      : undefined;
    if (definition.contractStatus && contractStatus !== definition.contractStatus) return [];
    if (definition.excludedContractStatuses?.includes(contractStatus)) return [];
    const currentValue = valueAtPath(currentPayload, definition.path);
    if (typeof currentValue !== 'number' || !Number.isFinite(currentValue)) {
      return [];
    }
    const rawSampleTime = definition.sampleTimePath
      ? valueAtPath(currentPayload, definition.sampleTimePath)
      : null;
    const rawDataStatus = definition.dataStatusPath
      ? valueAtPath(currentPayload, definition.dataStatusPath)
      : null;
    return [{
      path: definition.path,
      metric: definition.metric,
      unit: definition.unit,
      currentValue,
      equipmentId: valueAtPath(currentPayload, definition.equipmentIdPath) || null,
      sampleTime: typeof rawSampleTime === 'string' && rawSampleTime.length > 0 ? rawSampleTime : null,
      dataStatus: typeof rawDataStatus === 'string' && rawDataStatus.length > 0 ? rawDataStatus : null
    }];
  });
}

export {
  COUNTERS_BY_MESSAGE_TYPE,
  counterReadings
};
